import {
  HStack,
  Skeleton,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { lastDayOfMonth } from 'date-fns';
import React from 'react';
import { useQueryClient } from 'react-query';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Column } from 'react-table';

import { AppError } from '../../../../../framework/models/errors';
import { ListUtils } from '../../../../../framework/utils/ListUtils';
import { TypeUtils } from '../../../../../framework/utils/UtilityTypes';
import { useCheckArea } from '../../../../components/Check';
import ClickableText from '../../../../components/ClickableText';
import {
  AddButton,
  BackButton,
  DecideButton,
} from '../../../../components/StyledButton';
import StyledTable from '../../../../components/StyledTable';
import { pageKeywords, pageNames, pagePaths } from '../../../../constants/Page';
import { useGlobalStateMachine } from '../../../../GlobalStore';
import {
  DateChangerState,
  generateInitialState,
  getStateFromDate,
  useDateChangers,
} from '../../../../hooks/DateChangers';
import { useEngeneNavigate } from '../../../../hooks/navigate';
import { usePostDeadlineSearch } from '../../../../hooks/queries/deadline';
import {
  EXPENSE_APPLY_CONTEXT_NAMES,
  usePostExpenseApplyApply,
  usePostExpenseApplySearch,
  usePostExpenseApplySingleSearch,
} from '../../../../hooks/queries/expenseApply';
import useToastHook from '../../../../hooks/ToastHook';
import {
  DeadlineMode,
  ExpenseApplySingleSearchResult,
  ExpenseWithAttachFiles,
  MasterKey,
  MatterSummary,
} from '../../../../models/gen/models';
import { Formatter } from '../../../../utils/FormatUtils';
import { useMasterContext } from '../../../MasterProvider';
import { useConfirmModal } from '../../../modals/confirm';
import { MatterDetailModal } from '../../../modals/MatterDetail';
import { useSizeContext } from '../../../SizeProvider';
import Wireframe from '../../../wireframe/Wireframe';

type DatasourceType = {
  customerName?: string;
  date?: string;
  expenseId?: number;
  expenseItemId?: number;
  matterId?: number;
  matterName?: string;
  price?: number;
  receipt?: boolean;
};

/**
 * 交通費・経費申請画面
 *
 * @returns
 */
const ExpenseReimbursement = () => {
  const { actions, state } = useGlobalStateMachine();
  if (TypeUtils.isNil(state.userInfo)) {
    return <Navigate to={pagePaths.ROOT} />;
  }

  const master = useMasterContext();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { toast } = useToastHook();
  const { CheckArea, checked, off } = useCheckArea();
  const { ConfirmModal, onOpen: onOpenConfirm } = useConfirmModal();
  const data = queryClient.getQueryData<ExpenseApplySingleSearchResult>(
    EXPENSE_APPLY_CONTEXT_NAMES.POST_EXPENSE_APPLY_SINGLE_SEARCH
  );
  const setData = React.useCallback(
    (result: ExpenseApplySingleSearchResult) =>
      queryClient.setQueryData(
        EXPENSE_APPLY_CONTEXT_NAMES.POST_EXPENSE_APPLY_SINGLE_SEARCH,
        result
      ),
    [queryClient]
  );
  const postExpenseApplySearch = usePostExpenseApplySearch();
  const postExpenseApplySingleSearch = usePostExpenseApplySingleSearch();
  const postExpenseApply = usePostExpenseApplyApply();
  const { data: deadlines, refetch: refetchDeadlines } = usePostDeadlineSearch({
    modes: [DeadlineMode.Expense],
  });
  const onChangeChanger = (dateState: DateChangerState) => {
    actions.updatePageInfo({ EXPENSE_REIMBURSEMENT: { dateState } });
    off();
    if (state.userInfo?.roleOptions.isOutsourcer) {
      postExpenseApplySearch.mutate(
        [
          {
            year: dateState.year,
            month: dateState.month,
            applyUnitId: master.applyUnit.expense1!.id,
          },
          {
            year: dateState.year,
            month: dateState.month,
            applyUnitId: master.applyUnit.expense2!.id,
          },
        ],
        {
          onSuccess: (results) => {
            if (TypeUtils.isNonNilArray(results)) {
              setData({
                reference: Object.values(
                  results.reduce(
                    (prev, cur) => ({
                      ...prev,
                      ...(cur.reference || []).reduce(
                        (prev2, cur2) => ({ ...prev2, [cur2.matterId!]: cur2 }),
                        {} as Record<number, MatterSummary>
                      ),
                    }),
                    {} as Record<number, MatterSummary>
                  )
                ),
                result: {
                  expenseApply: {},
                  expenses: Object.values(
                    results.reduce(
                      (prev, cur) => ({
                        ...prev,
                        ...(cur.result?.expenses || []).reduce(
                          (prev2, cur2) => ({
                            ...prev2,
                            [cur2.expense!.expenseId!]: cur2,
                          }),
                          {} as Record<number, ExpenseWithAttachFiles>
                        ),
                      }),
                      {} as Record<number, ExpenseWithAttachFiles>
                    )
                  ),
                },
              });
            }
          },
          onError: (error) =>
            toast(
              error instanceof AppError
                ? error
                : '経費申請情報の取得に失敗しました'
            ),
        }
      );
    } else {
      postExpenseApplySingleSearch.mutate(
        {
          year: dateState.year,
          month: dateState.month,
          applyUnitId: (dateState.half === 1
            ? master.applyUnit.expense1
            : master.applyUnit.expense2
          )?.id as number,
        },
        {
          onSuccess: (result) => {
            if (!TypeUtils.isNil(result)) {
              setData(result);
            }
          },
          onError: (error) =>
            toast(
              error instanceof AppError
                ? error
                : '経費申請情報の取得に失敗しました'
            ),
        }
      );
    }
  };
  const applyUnits = React.useMemo(
    () => ({
      1: master.applyUnit.expense1,
      2: master.applyUnit.expense2,
    }),
    [master.applyUnit]
  );
  const leftDeadend = React.useMemo(() => {
    if (!TypeUtils.isNilString(state.appConfig.facilitySettings?.PAST_LIMIT)) {
      const date = new Date();
      date.setMonth(
        date.getMonth() +
          (state.appConfig.facilitySettings!.PAST_LIMIT as number)
      );
      const startDay = master.applyUnit.attendance1?.startDay;
      if (TypeUtils.isNilNumber(startDay)) return undefined;
      date.setDate(startDay);
      return getStateFromDate(applyUnits, date);
    }
    if (!TypeUtils.isNilString(state.appConfig.facilitySettings?.START_DATE)) {
      return getStateFromDate(
        applyUnits,
        Formatter.createDate(
          state.appConfig.facilitySettings!.START_DATE as string
        )
      );
    }
    return undefined;
  }, [state.appConfig.facilitySettings]);
  const rightDeadend = React.useMemo(() => {
    if (
      !TypeUtils.isNilString(state.appConfig.facilitySettings?.FUTURE_LIMIT)
    ) {
      let date = new Date();
      date.setMonth(
        date.getMonth() +
          (state.appConfig.facilitySettings!.FUTURE_LIMIT as number)
      );
      const endDay = master.applyUnit.expense2?.endDay;
      if (TypeUtils.isNilNumber(endDay)) return undefined;
      if (endDay === 99) {
        date = lastDayOfMonth(date);
      } else date.setDate(endDay);
      return getStateFromDate(applyUnits, date);
    }
    return undefined;
  }, [state.appConfig.facilitySettings]);

  const sizes = useSizeContext();

  const {
    HalfMonthChanger,
    MonthChanger,
    setState,
    state: halfMonthState,
  } = useDateChangers({
    leftDeadend,
    rightDeadend,
    initialState:
      generateInitialState(searchParams, ['year', 'month', 'half']) ||
      state.pageInfo.EXPENSE_REIMBURSEMENT?.dateState,
    applyUnits,
  });
  const [selectedMasterId, setSelectedMatterId] = React.useState<number>();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const navigate = useEngeneNavigate();

  React.useState(() => {
    refetchDeadlines();
    onChangeChanger(halfMonthState);
  });
  React.useEffect(() => {
    const newState =
      generateInitialState(searchParams, ['year', 'month', 'half']) ||
      state.pageInfo.EXPENSE_REIMBURSEMENT?.dateState;
    onChangeChanger(newState);
    setState(newState);
  }, [searchParams]);
  const columns: ReadonlyArray<Column<DatasourceType>> = [
    {
      Header: '利用日',
      accessor: 'date',
    },
    {
      Header: '現場名',
      accessor: 'matterName',
    },
    {
      Header: '依頼元',
      accessor: 'customerName',
    },
    {
      Header: '費目',
      accessor: 'expenseItemId',
    },
    {
      Header: '金額',
      accessor: 'price',
    },
    {
      Header: '領収書',
      accessor: 'receipt',
    },
  ];
  const datasource: Array<DatasourceType> = TypeUtils.isNil(data)
    ? []
    : (data as ExpenseApplySingleSearchResult).result?.expenses
        ?.sort(ListUtils.sort((expense) => expense.expense?.date))
        .map((result) => {
          const matterSummary = data?.reference?.find(
            (s) => s.matterId === result.expense?.matterId
          );

          return {
            date: result.expense?.date,
            matterId: result.expense?.matterId,
            matterName: matterSummary?.matterName,
            customerName: matterSummary?.customerName,
            expenseId: result.expense?.expenseId,
            expenseItemId: result.expense?.expenseItemId,
            price: result.expense?.price,
            receipt: result.expense?.fileAttached,
          };
        }) || [];

  return (
    <Wireframe
      title={pageNames.EXPENSE_REIMBURSEMENT}
      topMessages={deadlines?.results}
    >
      <VStack width={sizes.width.styledMessageList}>
        {state.userInfo.roleOptions.isOutsourcer ? (
          <MonthChanger
            onChangeLeft={onChangeChanger}
            onChangeRight={onChangeChanger}
          />
        ) : (
          <HalfMonthChanger
            applyUnits={applyUnits}
            onChangeLeft={onChangeChanger}
            onChangeRight={onChangeChanger}
          />
        )}
        <Skeleton isLoaded={!postExpenseApplySearch.isLoading} width="100%">
          <StyledTable
            columns={columns}
            customizeRender={({ cell, data: original }) =>
              cell.column.id === 'date' &&
              !TypeUtils.isNilString(original.date) ? (
                <>{Formatter.dateOfWeekString(original.date)}</>
              ) : cell.column.id === 'matterName' ? (
                <ClickableText
                  hasLink={!TypeUtils.isNilNumber(original.matterId)}
                  onClick={() => {
                    setSelectedMatterId(original.matterId);
                    onOpen();
                  }}
                >
                  {cell.render('Cell')}
                </ClickableText>
              ) : cell.column.id === 'expenseItemId' ? (
                <ClickableText
                  onClick={() =>
                    navigate(
                      `${pagePaths.EXPENSE_DETAIL}/${original.expenseId}/${
                        TypeUtils.isNonNil(data?.result?.expenseApply)
                          ? pageKeywords.mode.VIEW
                          : pageKeywords.mode.EDIT
                      }`
                    )
                  }
                >
                  {
                    master.getBy(
                      MasterKey.ExpenseItems,
                      original.expenseItemId!
                    )?.name
                  }
                </ClickableText>
              ) : cell.column.id === 'price' ? (
                <Text textAlign="right">
                  {original.price?.toLocaleString()}
                </Text>
              ) : cell.column.id === 'receipt' ? (
                original.receipt ? (
                  <>あり</>
                ) : (
                  <></>
                ) // <Icon as={FiFileText} />
              ) : (
                <>{cell.render('Cell')}</>
              )
            }
            data={datasource}
          />
          <HStack justifyContent="space-between" marginY={2} width="100%">
            <AddButton
              hidden={TypeUtils.isNonNilObject(data?.result?.expenseApply)}
              onClick={() => navigate(pagePaths.EXPENSE_DETAIL)}
            />
            <Text>
              合計 ￥{' '}
              {TypeUtils.isNil(data)
                ? 0
                : (data as ExpenseApplySingleSearchResult).result?.expenses
                    ?.reduce((prev, cur) => prev + (cur.expense?.price || 0), 0)
                    .toLocaleString()}{' '}
              -
            </Text>
          </HStack>
          <CheckArea
            additionalArea={
              <Text color="red" fontWeight="bold">
                !!! 注意 !!! 一度確定すると原則訂正できません
              </Text>
            }
            button={DecideButton}
            buttonProps={{ isDisabled: !checked }}
            confirmMessage="経費入力の確定に同意します"
            doneMessage={
              TypeUtils.isNonNilString(data?.result?.expenseApply?.approveTime)
                ? '承認済です'
                : TypeUtils.isNonNilString(
                    data?.result?.expenseApply?.applyTime
                  )
                ? '申請済です'
                : undefined
            }
            // 外注は申請させずに経費一覧を見せるだけ
            hidden={state.userInfo?.roleOptions?.isOutsourcer}
            onClick={() => onOpenConfirm()}
          />
        </Skeleton>
        <HStack width="100%">
          <BackButton onClick={() => navigate(-1)} />
        </HStack>
      </VStack>
      <MatterDetailModal
        isOpen={isOpen}
        matterId={selectedMasterId}
        onClose={() => {
          setSelectedMatterId(undefined);
          onClose();
        }}
      />
      <ConfirmModal
        message={
          <VStack>
            <Text
              fontSize={sizes.fontSize.modalBodyTitle}
              fontWeight="bold"
            >{`${halfMonthState.year}年${halfMonthState.month
              .toString()
              .padStart(2, '0')}月${
              halfMonthState.half === 1 ? '前半' : '後半'
            }`}</Text>
            <Text>
              (
              {Formatter.range(
                halfMonthState.year,
                halfMonthState.month - 1,
                halfMonthState.half === 1
                  ? master.applyUnit.expense1
                  : master.applyUnit.expense2
              )}
              )
            </Text>
            <Text>の経費申請を行います。よろしいですか？</Text>
            <Text color="red" fontWeight="bold">
              一度確定すると原則訂正できません
            </Text>
          </VStack>
        }
        onOk={() => {
          actions.setGuard(true);
          postExpenseApply.mutate(
            {
              yearMonth: `${halfMonthState.year}-${halfMonthState.month
                .toString()
                .padStart(2, '0')}-01`,
              applyUnitId:
                halfMonthState.half === 1
                  ? master.applyUnit.expense1?.id
                  : master.applyUnit.expense2?.id,
              userCodeWorker: state.userInfo?.user_code,
            },
            {
              onSuccess: () => {
                refetchDeadlines();
                navigate();
                actions.setGuard(false);
              },
              onError: (error) => {
                toast(
                  error instanceof AppError ? error : '経費申請に失敗しました'
                );
                actions.setGuard(false);
              },
            }
          );
        }}
      />
    </Wireframe>
  );
};

export default ExpenseReimbursement;
